import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_NOTIFICATION_SETTINGS,
  GET_SYSTEM_NOTIFICATION_CONFIG,
  GET_NOTIFICATION_PREVIEW,
  GET_GUEST_WORK_ORDER_NOTIFICATION_PREVIEW,
  UPDATE_NOTIFICATION_SETTING,
  UPDATE_SYSTEM_NOTIFICATION_CONFIG,
  SEND_TEST_NOTIFICATION,
  SEND_TEST_NOTIFICATION_BY_TYPE,
  INITIALIZE_DEFAULT_NOTIFICATION_SETTINGS,
  DISABLE_NOTIFICATION_FOR_ALL_USERS,
  SEND_JOB_ALERTS_TO_ASSIGNED_USERS
} from '../graphql/notifications';

export default function NotificationsSection() {
  // State for system notification config
  const [testMode, setTestMode] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [callCenterNotificationEmail, setCallCenterNotificationEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [sendingTestForType, setSendingTestForType] = useState('');
  const [disablingUsersForType, setDisablingUsersForType] = useState('');
  const [isUpdating, setIsUpdating] = useState(false); // Single updating state
  const [isSendingJobAlerts, setIsSendingJobAlerts] = useState(false);
  const [isSavingCallCenterEmail, setIsSavingCallCenterEmail] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewChannel, setPreviewChannel] = useState('email');

  const notificationTypeLabels = {
    WORK_ORDER_COMPLETED: 'Work Order Completed',
    WORK_ORDER_COMMENT_CALL_CENTER: 'Work Order Comment (Reservation Team)',
    TIME_OFF_REQUEST: 'Team Availability Requests',
    TIME_OFF_DECISION: 'Time-Off Decisions',
  };

  const guestWorkOrderNotifications = [
    { event: 'CREATED', label: 'Work Order Created', description: 'Sent when the guest service request is opened without an initial assignment.' },
    { event: 'CREATED_ASSIGNED', label: 'Work Order Created + Assigned', description: 'Combined alert sent when a team member is already assigned at creation.' },
    { event: 'ASSIGNED', label: 'Team Member Assigned', description: 'Sent for the first assignment when the work order was initially unassigned.' },
    { event: 'COMPLETED', label: 'Work Order Completed', description: 'Sent when the guest service request is marked complete.' },
  ];
  
  // State for Guest App notifications - ALL OFF by default for safety
  const [guestAppSettings, setGuestAppSettings] = useState({
    pushNotifications: false,
    emailNotifications: false,
    smsNotifications: false
  });
  
  // Queries
  const { 
    data: settingsData, 
    loading: settingsLoading, 
    error: settingsError,
    refetch: refetchSettings
  } = useQuery(GET_NOTIFICATION_SETTINGS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
  
  const { 
    data: configData, 
    loading: configLoading, 
    error: configError,
    refetch: refetchConfig
  } = useQuery(GET_SYSTEM_NOTIFICATION_CONFIG);

  const [loadNotificationPreview, { loading: previewLoading }] = useLazyQuery(
    GET_NOTIFICATION_PREVIEW,
    { fetchPolicy: 'no-cache' },
  );
  const [loadGuestWorkOrderPreview, { loading: guestPreviewLoading }] = useLazyQuery(
    GET_GUEST_WORK_ORDER_NOTIFICATION_PREVIEW,
    { fetchPolicy: 'no-cache' },
  );
  
  // Mutations
  const [updateSetting] = useMutation(UPDATE_NOTIFICATION_SETTING, {
    onCompleted: () => {
      setIsUpdating(false);
      refetchSettings(); // Ensure fresh data
    },
    onError: (error) => {
      setIsUpdating(false);
      toast.error(`Failed to update notification setting: ${error.message}`);
      refetchSettings(); // Restore correct state
    }
  });
  
  const [updateConfig] = useMutation(UPDATE_SYSTEM_NOTIFICATION_CONFIG, {
    onError: (error) => {
      toast.error(`Failed to update system configuration: ${error.message}`);
    }
  });
  
  const [sendTestNotification] = useMutation(SEND_TEST_NOTIFICATION, {
    onCompleted: () => {
      toast.success('Test notification sent!');
      setIsSendingTest(false);
    },
    onError: (error) => {
      toast.error(`Failed to send test notification: ${error.message}`);
      setIsSendingTest(false);
    }
  });
  
  const [sendTestNotificationByType] = useMutation(SEND_TEST_NOTIFICATION_BY_TYPE, {
    onCompleted: () => {
      toast.success(`Test notification sent!`);
      setSendingTestForType('');
    },
    onError: (error) => {
      toast.error(`Failed to send test notification: ${error.message}`);
      setSendingTestForType('');
    }
  });
  
  const [initializeSettings] = useMutation(INITIALIZE_DEFAULT_NOTIFICATION_SETTINGS, {
    onCompleted: () => {
      toast.success('Default notification settings initialized');
      refetchSettings();
    },
    onError: (error) => {
      toast.error(`Failed to initialize settings: ${error.message}`);
    }
  });

  const [disableNotificationForAllUsers] = useMutation(DISABLE_NOTIFICATION_FOR_ALL_USERS, {
    onCompleted: (data) => {
      const affectedUsers = data.disableNotificationForAllUsers;
      toast.success(`Defaulted this alert off and updated ${affectedUsers} active users`);
      setDisablingUsersForType('');
    },
    onError: (error) => {
      toast.error(`Failed to turn off user alerts: ${error.message}`);
      setDisablingUsersForType('');
    }
  });
  
  const [sendJobAlertsToAssignedUsers] = useMutation(SEND_JOB_ALERTS_TO_ASSIGNED_USERS, {
    onCompleted: (data) => {
      const { notifiedUsers, totalJobs, userDetails } = data.sendJobAlertsToAssignedUsers;
      toast.success(`Job alerts sent to ${notifiedUsers} users for ${totalJobs} total jobs`);
      setIsSendingJobAlerts(false);
      
      // Show detailed results if available
      if (userDetails && userDetails.length > 0) {
        console.log('Job alert details:', userDetails);
      }
    },
    onError: (error) => {
      toast.error(`Failed to send job alerts: ${error.message}`);
      setIsSendingJobAlerts(false);
    }
  });
  
  // Update local state when data loads
  useEffect(() => {
    if (configData?.systemNotificationConfig) {
      setTestMode(configData.systemNotificationConfig.testModeEnabled);
      setTestEmail(configData.systemNotificationConfig.testEmail || '');
      setTestPhoneNumber(configData.systemNotificationConfig.testPhoneNumber || '');
      setCallCenterNotificationEmail(configData.systemNotificationConfig.callCenterNotificationEmail || '');
    }
  }, [configData]);
  
  // Handle test mode toggle
  const handleToggleTestMode = async () => {
    await updateConfig({
      variables: {
        input: {
          testModeEnabled: !testMode
        }
      }
    });
    setTestMode(!testMode);
  };
  
  // Handle saving system config
  const handleSaveConfig = async () => {
    await updateConfig({
      variables: {
        input: {
          testEmail,
          testPhoneNumber
        }
      }
    });
    toast.success('Test configuration saved');
  };

  const handleSaveCallCenterEmail = async () => {
    setIsSavingCallCenterEmail(true);
    try {
      await updateConfig({
        variables: {
          input: {
            callCenterNotificationEmail: callCenterNotificationEmail.trim()
          }
        }
      });
      await refetchConfig();
      toast.success('Reservation Team email saved');
    } catch (error) {
      // onError toast already handled by mutation
    } finally {
      setIsSavingCallCenterEmail(false);
    }
  };
  
  // Handle sending test notification
  const handleSendTest = async () => {
    setIsSendingTest(true);
    await sendTestNotification({
      variables: {
        email: testEmail,
        phoneNumber: testPhoneNumber
      }
    });
  };
  
  // Handle sending test notification by type
  const handleSendTestByType = async (notificationType) => {
    if (!testEmail) {
      toast.error('Please enter a test email address first');
      return;
    }
    
    setSendingTestForType(notificationType);
    await sendTestNotificationByType({
      variables: {
        notificationType,
        email: testEmail
      }
    });
  };

  const handlePreview = async (notificationType) => {
    setPreviewChannel('email');
    setPreview({ audience: 'team', notificationType, loading: true });
    try {
      const { data } = await loadNotificationPreview({ variables: { notificationType } });
      setPreview(data?.notificationPreview ? { ...data.notificationPreview, audience: 'team' } : null);
    } catch (error) {
      setPreview(null);
      toast.error(`Unable to load preview: ${error.message}`);
    }
  };

  const handleGuestWorkOrderPreview = async (event) => {
    setPreviewChannel('email');
    setPreview({ audience: 'guest', event, loading: true });
    try {
      const { data } = await loadGuestWorkOrderPreview({ variables: { event } });
      setPreview(data?.guestWorkOrderNotificationPreview
        ? { ...data.guestWorkOrderNotificationPreview, audience: 'guest' }
        : null);
    } catch (error) {
      setPreview(null);
      toast.error(`Unable to load guest preview: ${error.message}`);
    }
  };

  const handleDisableForAllUsers = async (notificationType) => {
    const typeLabel = notificationTypeLabels[notificationType] || notificationType.replace(/_/g, ' ');
    const confirmed = window.confirm(
      `Turn off ${typeLabel} by default?\n\nThis will set each active user's email and SMS preference for this alert to off and make it default off for future users. The global role settings will stay unchanged.`
    );

    if (!confirmed) {
      return;
    }

    setDisablingUsersForType(notificationType);
    await disableNotificationForAllUsers({
      variables: {
        notificationType,
      },
    });
  };
  
  // Handle sending job alerts to assigned users
  const handleSendJobAlerts = async () => {
    setIsSendingJobAlerts(true);
    await sendJobAlertsToAssignedUsers();
  };
  
  // Handle Guest App notification toggles
  const handleGuestAppToggle = (notificationType) => {
    setGuestAppSettings(prev => ({
      ...prev,
      [notificationType]: !prev[notificationType]
    }));
    
    const typeLabel = notificationType === 'pushNotifications' ? 'Push notifications' :
                      notificationType === 'emailNotifications' ? 'Email notifications' :
                      'SMS notifications';
    
    toast.success(`Guest App ${typeLabel} ${!guestAppSettings[notificationType] ? 'enabled' : 'disabled'}`);
  };

  // Handle toggling notification setting with useCallback to prevent re-renders
  const handleToggleNotification = useCallback(async (setting, field, value) => {
    // Prevent concurrent updates
    if (isUpdating) {
      return;
    }
    
    const updateData = {};
    updateData[field] = !value;
    
    setIsUpdating(true);
    
    try {
      await updateSetting({
        variables: {
          id: setting.id,
          input: updateData
        }
      });
      
      // Success handled in onCompleted
      toast.success(`${field === 'emailEnabled' ? 'Email' : 'SMS'} notification ${!value ? 'enabled' : 'disabled'}`);
      
    } catch (error) {
      console.error("Error toggling notification:", error);
      toast.error(`Failed to update setting: ${error.message}`);
    }
  }, [isUpdating, updateSetting]);
  
  // Group settings by role with stable sorting - only recompute when data actually changes
  const groupedSettings = React.useMemo(() => {
    if (!settingsData?.notificationSettings) return {};
    
    // Filter out PASSWORD_RESET notifications - they should not be configurable
    const configurableSettings = settingsData.notificationSettings.filter(
      setting => setting.notificationType !== 'PASSWORD_RESET'
    );
    
    // Create a stable sort by using all identifying fields
    const sortedSettings = [...configurableSettings].sort((a, b) => {
      // First by role, then by notification type, then by id for ultimate stability
      if (a.role !== b.role) return a.role.localeCompare(b.role);
      if (a.notificationType !== b.notificationType) return a.notificationType.localeCompare(b.notificationType);
      return a.id.localeCompare(b.id);
    });
    
    const result = sortedSettings.reduce((acc, setting) => {
      if (!acc[setting.role]) {
        acc[setting.role] = [];
      }
      acc[setting.role].push(setting);
      return acc;
    }, {});
    
    return result;
  }, [settingsData?.notificationSettings]);
  
  if (settingsLoading || configLoading) {
    return <div className="p-4 bg-gray-100 rounded-md">Loading notification settings...</div>;
  }
  
  if (settingsError || configError) {
    return (
      <div className="p-4 bg-red-100 text-red-800 rounded-md">
        Error loading notification settings. 
        {settingsError && <p>{settingsError.message}</p>}
        {configError && <p>{configError.message}</p>}
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Notification Settings</h2>
        
        <div className="flex space-x-2">
          <button
            onClick={handleSendJobAlerts}
            disabled={isSendingJobAlerts}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {isSendingJobAlerts ? 'Sending...' : 'Send Job Alerts'}
          </button>
          
          <button
            onClick={() => initializeSettings()}
            className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            Initialize Default Settings
          </button>
        </div>
      </div>
      
      {/* Test Mode Settings */}
      <div className="mb-8 pb-6 border-b border-gray-200">
        <h3 className="text-md font-medium text-gray-700 mb-4">Test Mode Settings</h3>
        
        <div className="flex items-center mb-4">
          <div className="flex h-5 items-center">
            <input
              type="checkbox"
              id="test-mode"
              checked={testMode}
              onChange={handleToggleTestMode}
              className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
            />
          </div>
          <div className="ml-3">
            <label htmlFor="test-mode" className="font-medium text-gray-700">
              Enable test mode
            </label>
            <p className="text-sm text-gray-500">
              When enabled, all notifications will be sent to the test email and phone number instead of actual recipients.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mb-4">
          <div>
            <label htmlFor="test-email" className="block text-sm font-medium text-gray-700">Test Email</label>
            <input
              type="email"
              id="test-email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              placeholder="example@email.com"
            />
          </div>
          
          <div>
            <label htmlFor="test-phone" className="block text-sm font-medium text-gray-700">Test Phone Number</label>
            <input
              type="tel"
              id="test-phone"
              value={testPhoneNumber}
              onChange={(e) => setTestPhoneNumber(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              placeholder="+15555555555"
            />
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={handleSaveConfig}
            className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Save Settings
          </button>
          
          <button
            onClick={handleSendTest}
            disabled={isSendingTest || (!testEmail && !testPhoneNumber)}
            className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
          >
            {isSendingTest ? 'Sending...' : 'Send Test Notification'}
          </button>
        </div>
      </div>

      {/* Reservation Team Email Routing */}
      <div className="mb-8 pb-6 border-b border-gray-200">
        <h3 className="text-md font-medium text-gray-700 mb-4">Reservation Team Email Routing</h3>
        <p className="text-sm text-gray-500 mb-4">
          When set, Call Center / Reservation Team work-order emails (assignment, completed, escalated, and comments)
          for tickets they submitted are sent to this shared inbox instead of individual team member account emails.
          Leave blank to use personal emails. Test mode still redirects everything to the test email when enabled.
        </p>
        <div
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          role="note"
          aria-label="Call Center Property Care and Feedback notification note"
        >
          <span className="font-medium">Note for admins / developers:</span> Call Center fan-out alerts for
          {' '}<span className="font-medium">Property Care</span> and{' '}
          <span className="font-medium">Feedback</span> work orders are disabled in code
          (assignment, completion, and comments). Role toggles below do not re-enable those types.
          Guest Impact and other work-order types still notify the Reservation Team inbox when configured.
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mb-4">
          <div>
            <label htmlFor="call-center-notification-email" className="block text-sm font-medium text-gray-700">
              Reservation Team Email
            </label>
            <input
              type="email"
              id="call-center-notification-email"
              value={callCenterNotificationEmail}
              onChange={(e) => setCallCenterNotificationEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveCallCenterEmail();
                }
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              placeholder="reservations@sweetgrassproperties.com"
              aria-label="Reservation Team notification email"
              tabIndex={0}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleSaveCallCenterEmail}
          disabled={isSavingCallCenterEmail}
          className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          aria-label="Save Reservation Team email"
        >
          {isSavingCallCenterEmail ? 'Saving...' : 'Save Reservation Team Email'}
        </button>
      </div>
      
      {/* Guest App Notification Settings */}
      <div className="mb-8 pb-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-md font-medium text-gray-700">Guest App Notifications</h3>
            <p className="text-sm text-gray-500 mt-1">
              Control notifications sent to guests for the mobile app. 
              <span className="font-medium text-orange-600"> All disabled by default - app not ready for production.</span>
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-sm font-medium text-red-600">Production Safety Mode</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Push Notifications Toggle */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21l4-7 4 7M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">Push Notifications</p>
                  <p className="text-xs text-gray-500">Mobile app push alerts</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleGuestAppToggle('pushNotifications')}
                className={`${
                  guestAppSettings.pushNotifications ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    guestAppSettings.pushNotifications ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                guestAppSettings.pushNotifications 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {guestAppSettings.pushNotifications ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          {/* Email Notifications Toggle */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                  <p className="text-xs text-gray-500">Guest email alerts</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleGuestAppToggle('emailNotifications')}
                className={`${
                  guestAppSettings.emailNotifications ? 'bg-green-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    guestAppSettings.emailNotifications ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                guestAppSettings.emailNotifications 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {guestAppSettings.emailNotifications ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          {/* SMS Notifications Toggle */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                  <p className="text-xs text-gray-500">Guest text messages</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleGuestAppToggle('smsNotifications')}
                className={`${
                  guestAppSettings.smsNotifications ? 'bg-yellow-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    guestAppSettings.smsNotifications ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                guestAppSettings.smsNotifications 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {guestAppSettings.smsNotifications ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Warning when any guest notifications are enabled */}
        {(guestAppSettings.pushNotifications || guestAppSettings.emailNotifications || guestAppSettings.smsNotifications) && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-orange-800">
                  Guest App notifications are enabled
                </h3>
                <p className="text-sm text-orange-700 mt-1">
                  Make sure the guest mobile app is ready for production before enabling these notifications.
                  Guests will receive notifications about app downloads and updates.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Notification Settings by Role */}
      <div>
        <h3 className="text-md font-medium text-gray-700 mb-4">Notification Settings by Role</h3>
        
        {/* Work Order Assignment Info */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">Work Order Assignment Notifications</h4>
              <p className="text-sm text-blue-700 mt-1">
                These notifications are sent when work orders are assigned to users. 
                <span className="font-medium"> Admin, Maintenance, and Housekeeper Manager roles have email notifications enabled by default.</span>
                Users will receive notifications with work order details and direct links to view the assignment.
              </p>
            </div>
          </div>
        </div>
        
        {Object.entries(groupedSettings).map(([role, settings]) => (
          <div key={role} className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2 uppercase">
              {role.replace('_', ' ')}
            </h4>
            {role === 'CALL_CENTER' && (
              <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Property Care and Feedback work-order fan-out alerts for this role are disabled in application code
                (not controlled by the toggles below).
              </p>
            )}
            
            <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notification Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SMS
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {settings.map((setting) => (
                    <tr key={`${setting.role}-${setting.notificationType}-${setting.id}`} 
                        className={setting.notificationType === 'WORK_ORDER_ASSIGNMENT' || setting.notificationType === 'WORK_ORDER_COMPLETED' ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {notificationTypeLabels[setting.notificationType] || setting.notificationType.replace(/_/g, ' ')}
                          {(setting.notificationType === 'WORK_ORDER_ASSIGNMENT' || setting.notificationType === 'WORK_ORDER_COMPLETED') && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Work Orders
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <label className={`inline-flex items-center ${isUpdating ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                            {isUpdating ? (
                              <div className="h-5 w-5 rounded border border-gray-300 flex items-center justify-center">
                                <div className="h-3 w-3 rounded-full bg-sky-600 animate-pulse"></div>
                              </div>
                            ) : (
                              <input
                                type="checkbox"
                                checked={setting.emailEnabled}
                                onChange={() => handleToggleNotification(setting, 'emailEnabled', setting.emailEnabled)}
                                disabled={isUpdating}
                                className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer disabled:opacity-50"
                              />
                            )}
                            <span className="ml-2 text-sm text-gray-700">
                              {setting.emailEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <label className={`inline-flex items-center ${isUpdating ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                            {isUpdating ? (
                              <div className="h-5 w-5 rounded border border-gray-300 flex items-center justify-center">
                                <div className="h-3 w-3 rounded-full bg-sky-600 animate-pulse"></div>
                              </div>
                            ) : (
                              <input
                                type="checkbox"
                                checked={setting.smsEnabled}
                                onChange={() => handleToggleNotification(setting, 'smsEnabled', setting.smsEnabled)}
                                disabled={isUpdating}
                                className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer disabled:opacity-50"
                              />
                            )}
                            <span className="ml-2 text-sm text-gray-700">
                              {setting.smsEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handlePreview(setting.notificationType)}
                            disabled={previewLoading && preview?.notificationType === setting.notificationType}
                            className="px-2 py-1 text-xs border border-sky-300 text-sky-700 rounded hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                          >
                            {previewLoading && preview?.notificationType === setting.notificationType ? 'Loading...' : 'Preview'}
                          </button>
                          <button
                            onClick={() => handleSendTestByType(setting.notificationType)}
                            disabled={!testEmail || sendingTestForType === setting.notificationType || isUpdating || disablingUsersForType === setting.notificationType}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {sendingTestForType === setting.notificationType ? 'Sending...' : 'Send Test Email'}
                          </button>
                          <button
                            onClick={() => handleDisableForAllUsers(setting.notificationType)}
                            disabled={isUpdating || disablingUsersForType === setting.notificationType}
                            className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {disablingUsersForType === setting.notificationType ? 'Turning Off...' : 'Default Off'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        
        {Object.keys(groupedSettings).length === 0 && (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No notification settings found. Click "Initialize Default Settings" to create them.</p>
          </div>
        )}
      </div>

      {/* Guest notification previews */}
      <div className="mt-10 border-t border-gray-200 pt-8">
        <div className="mb-5">
          <h3 className="text-md font-medium text-gray-700">Guest Notifications</h3>
          <p className="mt-1 text-sm text-gray-500">
            Preview messages sent directly to guests. These alerts depend on the work order's saved reservation and guest-alert eligibility, not the team role settings above.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {guestWorkOrderNotifications.map((notification) => (
            <div key={notification.event} className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">{notification.label}</h4>
                    <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">Guest Work Order</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{notification.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleGuestWorkOrderPreview(notification.event)}
                  disabled={guestPreviewLoading && preview?.event === notification.event}
                  className="shrink-0 rounded border border-emerald-400 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                >
                  {guestPreviewLoading && preview?.event === notification.event ? 'Loading...' : 'Preview'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" role="dialog" aria-modal="true" aria-labelledby="notification-preview-title">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 id="notification-preview-title" className="text-lg font-semibold text-gray-900">
                  {preview.audience === 'guest'
                    ? `${guestWorkOrderNotifications.find((notification) => notification.event === preview.event)?.label || preview.event.replace(/_/g, ' ')} Preview`
                    : `${notificationTypeLabels[preview.notificationType] || preview.notificationType.replace(/_/g, ' ')} Preview`}
                </h3>
                <p className="mt-1 text-xs text-gray-500">Uses representative information and the actual production template. Nothing is sent.</p>
              </div>
              <button type="button" onClick={() => setPreview(null)} className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close notification preview">
                <span aria-hidden="true" className="text-2xl leading-none">&times;</span>
              </button>
            </div>

            {preview.loading ? (
              <div className="p-10 text-center text-sm text-gray-500">Rendering preview...</div>
            ) : (
              <>
                <div className="flex gap-2 border-b border-gray-200 px-6 pt-4">
                  <button type="button" onClick={() => setPreviewChannel('email')} className={`rounded-t-md px-4 py-2 text-sm font-medium ${previewChannel === 'email' ? 'bg-sky-50 text-sky-700 border border-b-0 border-sky-200' : 'text-gray-500 hover:text-gray-700'}`}>Email</button>
                  <button type="button" onClick={() => setPreviewChannel('sms')} className={`rounded-t-md px-4 py-2 text-sm font-medium ${previewChannel === 'sms' ? 'bg-sky-50 text-sky-700 border border-b-0 border-sky-200' : 'text-gray-500 hover:text-gray-700'}`}>SMS</button>
                </div>
                <div className="overflow-y-auto p-6">
                  {previewChannel === 'email' ? (
                    <div>
                      <div className="mb-4 rounded-md bg-gray-50 px-4 py-3 text-sm"><span className="font-semibold text-gray-700">Subject:</span> <span className="text-gray-900">{preview.emailSubject}</span></div>
                      <iframe title={`${preview.notificationType || preview.event} email preview`} srcDoc={preview.emailHtml} sandbox="" className="h-[520px] w-full rounded-md border border-gray-200 bg-white" />
                    </div>
                  ) : (
                    <div className="mx-auto max-w-sm rounded-[2rem] border-8 border-gray-800 bg-gray-100 p-5 shadow-inner">
                      <div className="mb-4 text-center text-xs font-medium text-gray-500">Sweetgrass Properties</div>
                      {preview.smsText ? (
                        <div className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-sm leading-5 text-gray-900 shadow-sm">{preview.smsText}</div>
                      ) : (
                        <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">This notification type does not currently have an SMS template.</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 
